import { useEffect } from 'react';
import { create } from 'zustand';

interface OnlineState {
  isOnline: boolean;
  setOnline: (v: boolean) => void;
}

export const useOnlineStore = create<OnlineState>((set) => ({
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  setOnline: (v) => set({ isOnline: v }),
}));

let listenersMounted = false;

/**
 * Mount once at app start. Subscribes to browser `online`/`offline` events.
 * We trust the browser events; a deeper reachability ping can be added later
 * if cellular drops without firing them in the field.
 */
export function mountOnlineStatusListeners(): void {
  if (listenersMounted || typeof window === 'undefined') return;
  listenersMounted = true;
  const set = useOnlineStore.getState().setOnline;
  window.addEventListener('online', () => set(true));
  window.addEventListener('offline', () => set(false));
}

/** Read-only synchronous check — use outside React components. */
export function isOnline(): boolean {
  return useOnlineStore.getState().isOnline;
}

/** React hook — re-renders the consumer when connectivity flips. */
export function useOnlineStatus(): boolean {
  const isOnlineNow = useOnlineStore((s) => s.isOnline);
  useEffect(() => {
    mountOnlineStatusListeners();
  }, []);
  return isOnlineNow;
}
